import React, { useEffect, useRef, useState } from "react";
// The app-set glyph the Guide table already shows (kept until the copy icon's design is
// reviewed — see its @deprecated note), so the two tables stay identical on screen.
import { CopyIcon } from "Components/DesignSystem/Iconography/app";

/**
 * How much of an ID is shown in a grid cell.
 *
 * The IDs are 32-character hex, and nobody reads one — they copy it. Printing all 32
 * spent a quarter of an ID column's width on a string that carries no meaning at a
 * glance, so the cell shows the leading 10 and the copy button hands over the whole
 * thing untouched.
 *
 * **Why a slice and not just `tw-truncate`.** `truncate` clips at whatever the column
 * width allows — so the visible character count drifted with viewport width and with
 * the font's own metrics (hex digits are not equal width in a proportional face). A
 * slice is exact and identical everywhere; `truncate` stays underneath as the backstop
 * for a very narrow window.
 *
 * Ten hex digits is 40 bits, and several of these IDs are time-based (note the `11f0`
 * / `11f1` version field), so the leading characters are what vary fastest between
 * rows. Collisions are not a practical concern at this scale.
 */
export const ID_VISIBLE_CHARS = 10;

export const shortenId = (id: string) =>
    id.length > ID_VISIBLE_CHARS ? `${id.slice(0, ID_VISIBLE_CHARS)}…` : id;

/** How long the "Copied" confirmation replaces the copy icon. */
export const COPIED_MS = 1000;

const fallbackCopyText = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
        document.execCommand("copy");
    } catch {
        // ignore copy failures
    }
    document.body.removeChild(textArea);
};

export const copyTextToClipboard = (text: string) => {
    if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).catch(() => fallbackCopyText(text));
        return;
    }
    fallbackCopyText(text);
};

export type CopyableIdProps = {
    /** The full ID. Empty renders "-" and no copy button. */
    id?: string | null;
    /**
     * Element-id stem. The cell is `${idPrefix}-${suffix}`, the button
     * `${idPrefix}-copy-${suffix}`, the confirmation `${idPrefix}-copied-${suffix}` —
     * the Guide table's `model-id-<row>` scheme, which its specs target.
     */
    idPrefix: string;
    /** Row-unique part of the element ids (a row index, or the ID itself). */
    suffix: string | number;
    /** `data-testid` of the copy button; defaults to the button's element id. */
    copyButtonTestId?: string;
};

/**
 * An ID cell: the first ten characters, the full ID on hover (`title`), and a copy
 * button that swaps to a green "Copied" for one second.
 *
 * Extracted from the Guide table (`ISRProjectTable`, US 13549) so the Products table
 * gets the same interaction instead of its old hover-portal tooltip, which had to be
 * chased with the cursor and vanished the moment the cursor left it.
 */
const CopyableId = ({ id, idPrefix, suffix, copyButtonTestId }: CopyableIdProps) => {
    const [copied, setCopied] = useState<boolean>(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }, []);

    const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
        // The row may itself be clickable (navigation, selection) — the copy is not a row click.
        e.stopPropagation();
        if (!id) return;
        copyTextToClipboard(id);
        setCopied(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setCopied(false), COPIED_MS);
    };

    return (
        <div id={`${idPrefix}-${suffix}`} title={id ?? ""} className="tw-flex tw-w-full tw-items-center tw-gap-2">
            {/* The shortened form is a VISUAL abbreviation, so it is hidden from
                assistive tech and the full ID is carried beside it. Without the pair,
                the cell would announce as "9282745b23 ellipsis" — the one thing an ID
                is for is being read out or copied in full. The `title` above is what a
                sighted user hovers for. */}
            {/* One box width in every row, so the copy button lines up down the column.
                Ten hex characters are NOT one width in Noto Sans (measured 2026-09-24:
                87–93px at 14px, "dddddddddd…" 6.94em), so a content-sized span put each
                button wherever its own ID ended. `flex-1` up to `max-w-[7em]`: every
                row grows to the same 7em, and in a narrow column all rows shrink
                together; `min-w-0` lets `truncate` still clip. `w-full` on the row:
                table cells wrap children in their own flex row, which would
                shrink-wrap this one and leave `flex-1` nothing to grow into (§ 323). */}
            <span className="tw-flex-1 tw-min-w-0 tw-max-w-[7em] tw-truncate" aria-hidden="true">{id ? shortenId(id) : "-"}</span>
            <span className="tw-sr-only">{id || "-"}</span>
            {id ? (
                copied ? (
                    <span id={`${idPrefix}-copied-${suffix}`} className="tw-flex-shrink-0 tw-text-tone-success-ink tw-text-xs tw-font-medium">Copied</span>
                ) : (
                    <button
                        type="button"
                        data-testid={copyButtonTestId ?? `${idPrefix}-copy-${suffix}`}
                        id={`${idPrefix}-copy-${suffix}`}
                        className="tw-flex-shrink-0 tw-text-accent"
                        title="Copy ID"
                        aria-label="Copy ID"
                        onClick={handleCopy}
                    >
                        <CopyIcon />
                    </button>
                )
            ) : null}
        </div>
    );
};

export default CopyableId;
